import { env } from './env';
import { markPasswordChangeRequired } from './session';
import { endSessionAndRedirect, refreshSession } from './token-refresh';

/**
 * Thin fetch wrapper over the real API, `/api/v1`-prefixed to match
 * every route exercised throughout this project's manual and automated
 * testing (apps/api main.ts mounts no global prefix itself — the `/api/v1`
 * segment is part of each controller's own route path).
 *
 * `X-Shop-Id` is attached whenever a caller supplies one. Its absence on
 * every merchant-scoped write was a confirmed, diagnosed bug in an earlier,
 * ad-hoc test script for this same project (never a server-side bug) — this
 * client exists specifically so that mistake cannot recur silently: the
 * header is one parameter here, not something each call site re-implements.
 */

/**
 * DT-4 / P1-2, revised: `AuthenticatedGuard`
 * (apps/api/src/modules/identity/http/authenticated.guard.ts) is the ONLY
 * place a 401 means "this session's token is no longer good — sign in
 * again"; every other 401 is a handler's own deliberate, recoverable
 * rejection with a token that is still perfectly valid (`POST /auth/login`
 * with the wrong password sends no token at all).
 *
 * This used to be told apart by matching the exact English string
 * `'Authentication required.'` — fragile the moment that message changed
 * for any reason, including translation. The API now attaches a stable
 * `errorCode: 'SESSION_EXPIRED'` (`AllExceptionsFilter`,
 * `SessionExpiredException`) for exactly this condition; that code, never
 * the message text, is what the check below matches on.
 */
const SESSION_EXPIRED_ERROR_CODE = 'SESSION_EXPIRED';

/**
 * The account holds a PROVISIONAL password (issued by a superadmin or a shop owner): the API refuses every route but the password change
 * with this code. The session is not dead - the person must choose a password - so it is sent to that screen, not to the login.
 */
const PASSWORD_CHANGE_REQUIRED_ERROR_CODE = 'PASSWORD_CHANGE_REQUIRED';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly body: unknown,
    /**
     * The stable code `@ecom/i18n`'s `resolveErrorMessageKey()` maps to a
     * translated, business-appropriate message — see that module's header
     * for why this exists instead of showing `.message` (the API's raw,
     * untranslated free text) to the user. `undefined` for a response with
     * no JSON body, or one from a server old enough not to send it yet.
     */
    readonly errorCode?: string,
    /**
     * The `Retry-After` header of a 429/503, in whole seconds, when the server sent a usable one — so a screen can say
     * « réessayez dans 12 s » instead of a bare « trop de tentatives ».
     */
    readonly retryAfterSeconds?: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ApiRequestOptions {
  readonly method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /**
   * A plain object/array is JSON-encoded with a `Content-Type: application/json`
   * header, matching every JSON endpoint in this API. A `FormData` (the
   * product-image upload, `product-image.controller.ts`'s
   * `FileInterceptor('file')`) is sent AS-IS, with no `Content-Type` set —
   * the browser computes the correct `multipart/form-data; boundary=...`
   * itself, and overriding it manually is a well-known way to corrupt the
   * boundary and break the upload silently.
   */
  readonly body?: unknown;
  readonly accessToken?: string | null;
  readonly shopId?: string | null;
  readonly signal?: AbortSignal;
  /** Extra request headers, e.g. `Idempotency-Key` on a manual order (never auth headers: those come from the fields above). */
  readonly headers?: Readonly<Record<string, string>>;
}

export function apiRequest<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  return send<T>(path, options, true);
}

async function send<T>(path: string, options: ApiRequestOptions, mayRenew: boolean): Promise<T> {
  const { method = 'GET', body, accessToken, shopId, signal, headers: extraHeaders } = options;

  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;

  const headers: Record<string, string> = { ...extraHeaders };
  if (body !== undefined && !isFormData) headers['Content-Type'] = 'application/json';
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
  if (shopId) headers['X-Shop-Id'] = shopId;

  // `exactOptionalPropertyTypes` forbids `signal: undefined` / `body: undefined`
  // as explicit values — RequestInit's own properties must be absent, not
  // present-with-undefined — hence building this object conditionally.
  const init: RequestInit = {
    method,
    headers,
    ...(body !== undefined ? { body: isFormData ? body : JSON.stringify(body) } : {}),
    ...(signal !== undefined ? { signal } : {}),
  };

  const response = await fetch(`${env.apiBaseUrl}/api/v1${path}`, init);

  const isJson = response.headers.get('content-type')?.includes('application/json') ?? false;
  const payload: unknown = isJson ? await response.json().catch(() => null) : null;

  if (!response.ok) {
    const isPayloadObject = payload !== null && typeof payload === 'object';
    const message =
      isPayloadObject && 'message' in payload
        ? String(payload.message)
        : `Request failed with status ${String(response.status)}.`;
    const errorCode =
      isPayloadObject && 'errorCode' in payload ? String(payload.errorCode) : undefined;

    // DT-4 / P1-2 — a dead session purges itself and leaves for /login
    // instead of every page it still touches staring at a stuck skeleton
    // or a bare "Authentication required." error forever (reproduced
    // live: the token invalidated, `RequireAuth` never re-checks because
    // it only asks "is a token PRESENT", and nothing else in this app
    // was watching for a 401 to say otherwise).
    //
    // Revised (« déconnexions répétées »): an EXPIRED access token is the normal end of its 15 minutes, not of the
    // session. It is renewed once (`token-refresh.ts`) and the call replayed with the new token; only a refresh the
    // API refuses ends the session. A renewal that could not run (network, 5xx) keeps the session: this call fails,
    // the next one tries again.
    if (response.status === 401 && errorCode === SESSION_EXPIRED_ERROR_CODE) {
      if (mayRenew && accessToken) {
        const renewal = await refreshSession(accessToken);
        if (renewal.kind === 'refreshed') {
          return send<T>(path, { ...options, accessToken: renewal.accessToken }, false);
        }
        if (renewal.kind === 'rejected') endSessionAndRedirect();
      } else {
        endSessionAndRedirect();
      }
    }

    if (response.status === 403 && errorCode === PASSWORD_CHANGE_REQUIRED_ERROR_CODE) {
      markPasswordChangeRequired();
      if (window.location.pathname !== '/change-password') {
        window.location.assign('/change-password');
      }
    }

    const retryAfter = Number(response.headers.get('retry-after'));
    throw new ApiError(
      message,
      response.status,
      payload,
      errorCode,
      Number.isFinite(retryAfter) && retryAfter > 0 ? Math.ceil(retryAfter) : undefined,
    );
  }

  return payload as T;
}
